import {useMemo} from "react";
import type {
    ChecklistData,
    ChecklistElements,
    ChecklistToc,
    CollectionDataResponse,
    CommentsResponse,
    Status,
} from "../../types";
import {
    computeHiddenIds,
    allExpanded,
    calculateCollectionProgress,
} from "../utils";

interface UseChecklistVisibilityParams {
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
    statusFilterSelected: Record<string, (Status | string)[]>;
    searchTerm: string;
    elements: ChecklistElements;
    openItems: Record<string, boolean>;
    collectionData: CollectionDataResponse | undefined;
    commentsData: CommentsResponse | undefined;
    checklistData: ChecklistData;
}

/**
 * Computes visibility, status mapping, progress, and expansion state
 * from the filtered TOC and current interaction state.
 *
 * Changes when: search/filter/expand state changes, or collection data updates.
 */
export const useChecklistVisibility = ({
    viewFilteredToc,
    effectiveRootNodeId,
    statusFilterSelected,
    searchTerm,
    elements,
    openItems,
    collectionData,
    commentsData,
    checklistData,
}: UseChecklistVisibilityParams) => {
    const {hiddenIds, searchHitIds, statusMap} = useMemo(() => {
        if (!viewFilteredToc || !effectiveRootNodeId) {
            return {
                hiddenIds: new Set<string>(),
                searchHitIds: new Set<string>(),
                statusMap: {} as Record<string, Status | -1>,
            };
        }
        return computeHiddenIds(
            viewFilteredToc,
            effectiveRootNodeId,
            statusFilterSelected,
            searchTerm,
            elements,
            collectionData,
            commentsData,
            checklistData.infoIds,
        );
    }, [
        viewFilteredToc,
        effectiveRootNodeId,
        statusFilterSelected,
        searchTerm,
        elements,
        collectionData,
        commentsData,
        checklistData,
    ]);

    const infoIdSet = useMemo(
        () => new Set(checklistData.infoIds ?? []),
        [checklistData.infoIds],
    );

    const hasOnlyInfoNodes = useMemo(() => {
        if (!effectiveRootNodeId) return false;
        const viewFilteredLeafIds = checklistData.leafNodeIds.filter(
            (id) => viewFilteredToc[id],
        );
        if (viewFilteredLeafIds.length === 0) return true;
        return viewFilteredLeafIds.every((id) => infoIdSet.has(id));
    }, [checklistData, viewFilteredToc, effectiveRootNodeId, infoIdSet]);

    const collectionProgress = useMemo(() => {
        if (!viewFilteredToc || !collectionData) {
            return {max: 1, value: 0};
        }
        return calculateCollectionProgress(
            viewFilteredToc,
            effectiveRootNodeId,
            collectionData,
            checklistData.infoIds,
        );
    }, [viewFilteredToc, effectiveRootNodeId, collectionData, checklistData]);

    const allItemsExpanded = useMemo(() => {
        if (!effectiveRootNodeId) return false;
        return allExpanded(
            viewFilteredToc,
            effectiveRootNodeId,
            openItems,
            hiddenIds,
        );
    }, [viewFilteredToc, effectiveRootNodeId, openItems, hiddenIds]);

    return {
        hiddenIds,
        searchHitIds,
        statusMap,
        infoIdSet,
        hasOnlyInfoNodes,
        collectionProgress,
        allItemsExpanded,
    };
};
